import { runtimeConfig } from "config/runtimeConfig";
import range from "lodash/range";
import React, { useState, useContext } from "react";
import RepeatSelect from "./RepeatSelect";
import HourRepeatSchedule from "./HourRepeatSchedule";
import DayRepeatSchedule from "./DayRepeatSchedule";
import WeekRepeatSchdule from "./WeekRepeatSchdule";
import { toast } from "Components/DesignSystem/Toast/useToast";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AuthManager from "../../../Common/AuthManager";
import Utils from "../../../Common/Utils";
import { AppContext } from "../../../Context/AppContext";
import Button from "Components/DesignSystem/Button/Button";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import DatePicker from "Components/DesignSystem/DatePicker/DatePicker";
import TimePicker from "Components/DesignSystem/TimePicker/TimePicker";
import Typography from "Components/DesignSystem/Typography/Typography";
import InfoTooltip from "Components/DesignSystem/Tooltip/InfoTooltip";

const MID_WAY_URL = runtimeConfig.CONNECTION_API;

const REPEAT_OPTIONS = [
  { value: "Never", label: "Never" },
  { value: "HOUR", label: "Hourly" },
  { value: "DAY", label: "Daily" },
  { value: "WEEK", label: "Weekly" },
];

const SCHEDULE_HELP =
  'The scheduled import will reflect after "XX" hours of the set UTC date and time. ' +
  "For example, If the import is set for every Monday at 2 PM and the recurring time " +
  "interval is 2 hours, then the import will start reflecting at 4 PM.";

/**
 * Schedule Import — start date/time, recurrence, and the POST to
 * `connection/{channelKey}/schedule`.
 *
 * On the design system since § 132. **`validateSchedule` and `buildScheduleData` are
 * unchanged** — the UTC string assembly, the `HOUR`-allows-equal-dates asymmetry, the
 * `runOnDay: [1]` default, and the `repeat === "Never"` branch that posts through
 * `createImport` instead of `createSchedule` all behave exactly as before.
 *
 * Three things did change, and each is a defect rather than a restyle:
 *
 * - **"Schedule Import" had no double-submit guard.** The line was
 *   `// disabled={disableButton}` — commented out, referring to a `disableButton`
 *   state that does not exist in this file. Two clicks created two schedules, and the
 *   endpoint has no idempotency key. `Import Now` in the parent form has always
 *   guarded this; the scheduling path never did.
 * - **The start date and time controls were mismatched and locale-dependent.** They
 *   came from `Admin/promotions/` — a directory whose module no longer exists and
 *   whose only consumer is this file — sized `108×36` and `138×37` by a stylesheet
 *   that also defined a **third** copy of the global `.drop-down-style`. The time
 *   field was a native `<input type="time">`, so the widget rendered in the
 *   *browser's* locale (12-hour AM/PM for most users) on a field labelled **UTC**,
 *   while the value stored was 24-hour. DS `DatePicker` / `TimePicker` are one height
 *   and unambiguously 24-hour.
 * - **`formatEndDate` is gone.** It was the fourth copy of the same zero-padding
 *   formatter in this codebase, and its comment said "format the date into dd-mm-yyyy"
 *   while it returned `yyyy-mm-dd`. `DatePicker` emits the ISO string itself.
 *
 * Two user-facing typos are corrected: the toast "Data import **shedule** failed." and
 * the weekday "**Wednessday**". Neither reaches a payload — the toast is copy, and
 * only `weeksInfo[i].code` is sent.
 */
const START_TIME_INVALID = "Please enter a valid start time";

const ScheduleForm = (props) => {
  const { validateForm, formData, fetchData } = props;
  const [scheduleInfo, setScheduleInfo] = useState({
    startDate: "",
    startTime: "",
    endDate: "",
    repeat: "Never",
    xDays: "1",
    xHours: "1",
    xWeeks: "1",
    weekly: [],
  });

  const appData = useContext(AppContext);
  const [weeksInfo, setWeeksInfo] = useState([
    { day: "Sunday", code: 6, displayChar: "S", selected: false },
    { day: "Monday", code: 0, displayChar: "M", selected: false },
    { day: "Tuesday", code: 1, displayChar: "T", selected: false },
    { day: "Wednesday", code: 2, displayChar: "W", selected: false },
    { day: "Thursday", code: 3, displayChar: "T", selected: false },
    { day: "Friday", code: 4, displayChar: "F", selected: false },
    { day: "Saturday", code: 5, displayChar: "S", selected: false },
  ]);
  let weeks = Array.from(range(1, 53));
  const [openDatePicker, setOpenDatePicker] = useState(false);
  /* The guard the `// disabled={disableButton}` comment promised and never delivered. */
  const [submitting, setSubmitting] = useState(false);
  /* AB#15442 — an incomplete start time is caught here, inline, before any API call. */
  const [startTimeError, setStartTimeError] = useState("");

  const handleScheduleChange = async (id, value) => {
    setScheduleInfo({ ...scheduleInfo, [id]: value });
  };

  /**
   * Toggle one weekday.
   *
   * Was a shallow copy followed by an in-place mutation of the day objects —
   * `newArray[i].selected = !newArray[i].selected` mutates the SAME object the
   * previous state holds, so the old state was silently rewritten too. It happened to
   * re-render because the array reference changed. `map` returns new objects.
   *
   * The unused first parameter is kept: `WeekRepeatSchdule` calls
   * `handleWeeklyDataChange(item, item.code)` and the signature is the contract
   * between them.
   */
  const handleWeeklyDataChange = (item, index) => {
    setWeeksInfo((prev) =>
      prev.map((day) => (day.code === index ? { ...day, selected: !day.selected } : day))
    );
  };

  const validateSchedule = () => {
    if (scheduleInfo.startDate === "" || scheduleInfo.startDate === undefined) {
      toast.error("Please select start date");
      return false;
    }

    // The picker reports '' until hour, minute and AM/PM are all set.
    if (scheduleInfo.startTime === "" || scheduleInfo.startTime === undefined) {
      setStartTimeError(START_TIME_INVALID);
      return false;
    }

    // Create a UTC Time string and
    // validate if start date is greater that today
    let combineDate =
      scheduleInfo.startDate + "T" + scheduleInfo.startTime + ":00";
    const _start_date = new Date(combineDate);
    const _today_local = new Date();
    let _combineDateToday = Utils.getUTCDateString(_today_local);
    const _today = new Date(_combineDateToday);
    if (_start_date < _today) {
      toast.error(
        "Start date and time should be greater than current time Note: UTC format"
      );
      return false;
    }

    if (scheduleInfo.repeat !== "Never") {
      if (scheduleInfo.endDate === "" || scheduleInfo.endDate === undefined) {
        toast.error("Please select end date");
        return false;
      }

      const _end_date = new Date(scheduleInfo.endDate);
      // check if end date is greater that start date
      if (scheduleInfo.repeat === "HOUR") {
        if (_end_date < _start_date) {
          toast.error("End date should be greater or equal to start date");
          return false;
        }
      } else {
        if (_end_date <= _start_date) {
          toast.error("End date should be greater than start date");
          return false;
        }
      }
    }
    return true;
  };

  function buildScheduleData(data) {
    let date = scheduleInfo.startDate;
    let time = scheduleInfo.startTime;

    let timeAndDate = date + "T" + time + ":00Z";

    let scheduleDetail = {
      ...data,
      startDate: new Date(timeAndDate).toISOString(),
      runAtVal: 0,
      runAtInterval: scheduleInfo.repeat,
      repeat: scheduleInfo.repeat,
      runOnDay: [1],
    };

    if (scheduleInfo.endDate !== "") {
      scheduleDetail.endDate = new Date(scheduleInfo.endDate).toISOString();
    }

    if (scheduleInfo.repeat === "HOUR") {
      scheduleDetail.runAtInterval = scheduleInfo.repeat;
      scheduleDetail.runAtVal = parseInt(scheduleInfo.xHours, 0);
    }

    if (scheduleInfo.repeat === "DAY") {
      scheduleDetail.runAtInterval = scheduleInfo.repeat;
      scheduleDetail.runAtVal = parseInt(scheduleInfo.xDays, 0);
    }

    if (scheduleInfo.repeat === "WEEK") {
      let days = [];
      for (let selected in weeksInfo) {
        if (weeksInfo[selected].selected === true) {
          days.push(weeksInfo[selected].code);
        }
      }
      scheduleDetail.runAtInterval = scheduleInfo.repeat;
      scheduleDetail.runAtVal = parseInt(scheduleInfo.xWeeks, 0);
      scheduleDetail.runOnDay = days;
    }
    return scheduleDetail;
  }

  const createSchedule = async () => {
    if (!validateForm()) return;
    if (!validateSchedule()) return;

    let scheduleDetail = buildScheduleData({ ...formData });

    // Persist the sync-to-environments selection (PUT connector/config/<channel_key>)
    // only now, on the Schedule Import action — never during selection.
    await props.persistSyncConfig?.();

    let res = await ServiceWrapper.doPost(
      `${MID_WAY_URL}connection/${appData?.channelKey}/schedule`,
      scheduleDetail,
      null,
      AuthManager.getGmsToken()
    );

    if (res?.data?.done) {
      toast.success("Schedule Data Import created successfully");
      fetchData();
      props.closeForm();
    } else {
      toast.error(res?.data?.message ?? "Data import schedule failed.");
    }
  };

  const createImport = async () => {
    if (!validateSchedule()) return;
    if (!validateForm()) return;

    let date = scheduleInfo.startDate;
    let time = scheduleInfo.startTime;
    let combineDate = date + "T" + time + ":00Z";
    let timeAndDate = new Date(combineDate).toISOString();

    let data = {
      ...formData,
      startDate: timeAndDate,
      repeat: "NEVER",
    };

    // Persist the sync-to-environments selection (PUT connector/config/<channel_key>)
    // only now, on the Schedule Import action — never during selection.
    await props.persistSyncConfig?.();

    let res = await ServiceWrapper.doPost(
      `${MID_WAY_URL}connection/${appData?.channelKey}/schedule`,
      data,
      null,
      AuthManager.getGmsToken()
    );

    if (res?.data?.done) {
      toast.success("Schedule Data Import created successfully");
      fetchData();
      props.closeForm();
    } else {
      toast.error(res?.data?.message ?? "Data import schedule failed.");
    }
  };

  /**
   * The submit path, with the double-submit guard the commented-out
   * `disabled={disableButton}` was reaching for.
   *
   * `finally` rather than a re-enable on each branch: `createSchedule` and
   * `createImport` both return early on a failed validation, and a guard that stayed
   * latched after "Please select start date" would leave the button dead with no way
   * to recover.
   */
  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (scheduleInfo.repeat === "Never") {
        await createImport();
      } else {
        await createSchedule();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tw-rounded-lg tw-border tw-border-edge tw-p-4" data-testid="connectors-schedule">
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Typography variant="title" as="h3">
            Schedule
          </Typography>
          <InfoTooltip message={SCHEDULE_HELP} label="About scheduled imports" placement="right" />
        </div>
      </div>

      {/*
        Sized to sit on ONE row inside the Schedule box: each field takes half
        the row minus half the 16px gap. `flex-wrap` is the fallback rather than the
        plan — at a smaller viewport the time field drops below the date rather than
        overflowing.

        **Both fields are the same width, deliberately.** The 150px here was sized
        against the OLD time control — two 74px selects — and once § 290 made the time
        field a single input like the date, two fields of different widths on one row
        read as a mistake rather than a hierarchy. They are the same kind of thing;
        they get the same box.
      */}
      <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-start tw-gap-4">
        <DatePicker
          label="Start Date"
          value={scheduleInfo.startDate}
          onChange={(_date, formatted) => handleScheduleChange("startDate", formatted)}
          minDate={new Date()}
          className="tw-w-[calc(50%-8px)]"
          data-testid="connectors-schedule-start-date"
        />
        <TimePicker
          label="Start Time(UTC)"
          value={scheduleInfo.startTime}
          onChange={(value) => {
            setStartTimeError("");
            handleScheduleChange("startTime", value);
          }}
          defaultMinute="00"
          errorMessage={startTimeError || undefined}
          className="tw-w-[calc(50%-8px)]"
          data-testid="connectors-schedule-start-time"
        />
      </div>

      <div className="tw-mt-4">
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
          <Dropdown
            label="Repeat"
            id="repeat"
            options={REPEAT_OPTIONS}
            value={scheduleInfo.repeat}
            onChange={(value) => handleScheduleChange("repeat", String(value))}
            className="tw-w-[150px]"
            usePortal
            data-testid="connectors-schedule-repeat"
          />

          {scheduleInfo.repeat === "HOUR" && (
            <RepeatSelect
              labelMessage={scheduleInfo.repeat}
              selectId="xHours"
              selectValue={scheduleInfo.xHours}
              selectOptions={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
              onChange={handleScheduleChange}
            />
          )}

          {scheduleInfo.repeat === "DAY" && (
            <RepeatSelect
              labelMessage={scheduleInfo.repeat}
              selectId="xDays"
              selectValue={scheduleInfo.xDays}
              selectOptions={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
              onChange={handleScheduleChange}
            />
          )}

          {scheduleInfo.repeat === "WEEK" && (
            <RepeatSelect
              labelMessage={scheduleInfo.repeat}
              selectId="xWeeks"
              selectValue={scheduleInfo.xWeeks}
              selectOptions={weeks}
              onChange={handleScheduleChange}
            />
          )}
        </div>

        {scheduleInfo.repeat === "HOUR" && (
          <HourRepeatSchedule
            scheduleInfo={scheduleInfo}
            openDatePicker={openDatePicker}
            setOpenDatePicker={setOpenDatePicker}
            setScheduleInfo={setScheduleInfo}
          />
        )}
        {scheduleInfo.repeat === "DAY" && (
          <DayRepeatSchedule
            scheduleInfo={scheduleInfo}
            openDatePicker={openDatePicker}
            setOpenDatePicker={setOpenDatePicker}
            setScheduleInfo={setScheduleInfo}
          />
        )}
        {scheduleInfo.repeat === "WEEK" && (
          <WeekRepeatSchdule
            repeat={scheduleInfo.repeat}
            weeksInfo={weeksInfo}
            scheduleInfo={scheduleInfo}
            openDatePicker={openDatePicker}
            handleWeeklyDataChange={handleWeeklyDataChange}
            setOpenDatePicker={setOpenDatePicker}
            setScheduleInfo={setScheduleInfo}
          />
        )}
      </div>

      <div className="tw-mt-4">
        <Button
          variant="primary"
          disabled={submitting}
          data-testid="connectors-schedule-submit-btn"
          onClick={handleSubmit}
        >
          Schedule Import
        </Button>
      </div>
    </div>
  );
};

export default ScheduleForm;
